import React from "react";
import type { MapObject, MapObjectCategory } from "@roll1/shared";
import { IconButton } from "./ui/icon-button";
import { ListRow } from "./ui/row";
import { Pencil, Trash2, Lock, Plus, Hexagon } from "lucide-react";
import { useI18n } from "../i18n";
import SidebarPanel from "./ui/SidebarPanel";
import { useCanvasContext } from "../contexts/CanvasContext";
import { useToolContext } from "../contexts/ToolContext";
import { useCanvasInteraction } from "../contexts/CanvasInteractionContext";

const CATEGORY_ICONS: Record<MapObjectCategory, string> = {
  furniture: "\u{1FA91}",
  terrain: "\u{1F3D4}\uFE0F",
  hazard: "\u26A0\uFE0F",
  door: "\u{1F6AA}",
  container: "\u{1F4E6}",
  light: "\u{1F4A1}",
  custom: "\u2B21",
};

const CATEGORY_I18N = {
  furniture: 'mapObject.catFurniture',
  terrain: 'mapObject.catTerrain',
  hazard: 'mapObject.catHazard',
  door: 'mapObject.catDoor',
  container: 'mapObject.catContainer',
  light: 'mapObject.catLight',
  custom: 'mapObject.catCustom',
} as const;

const MapObjectPanel = React.memo(function MapObjectPanel() {
  const { t } = useI18n();
  const { mapObjects } = useCanvasContext();
  const { setTool } = useToolContext();
  const { get } = useCanvasInteraction();

  const objects = mapObjects.objects.current;
  const selectedId = mapObjects.selectedObjectId.current;

  const handleCreateTool = () => setTool("map-object");
  const handleEdit = (obj: MapObject) => get("map-object:double-click")?.(obj);
  const handleDelete = (objectId: string) => get("map-object:delete")?.(objectId);
  const handleSelect = (objectId: string) => get("map-object:select")?.(objectId);

  return (
    <SidebarPanel
      id="map-objects"
      title={t('sb.objects')}
      icon={Hexagon}
      count={objects.length}
      defaultCollapsed
      tight
      actions={<IconButton label={t('sb.map.addObject')} icon={<Plus size={14} aria-hidden="true" />} onClick={handleCreateTool} />}
    >
      {objects.length === 0 ? <p className="sb-line py-1 text-meta text-dim">{t('sb.map.noObjects')}</p> : null}
      <ul className="flex flex-col">
        {objects.map((obj) => (
          <ListRow
            key={obj.id}
            current={selectedId === obj.id}
            lead={obj.style.icon || CATEGORY_ICONS[obj.category]}
            label={obj.label}
            onOpen={() => handleSelect(obj.id)}
            onOpenDouble={() => handleEdit(obj)}
            meta={t(CATEGORY_I18N[obj.category])}
            deco={obj.visibility === "all" ? [] : [{ icon: <Lock size={12} aria-hidden="true" />, label: t('mapObject.gmOnly') }]}
            actions={<IconButton size="sm" label={t('sb.obj.edit')} icon={<Pencil size={14} aria-hidden="true" />} onClick={() => handleEdit(obj)} />}
            menu={[
              { label: t('sb.obj.edit'), icon: Pencil, onSelect: () => handleEdit(obj) },
              { separator: true },
              {
                label: t('sb.delete'),
                icon: Trash2,
                danger: true,
                // No confirm here: the registered map-object:delete (MapObjectsManager) asks by the object's name — one
                // ConfirmDialog for the list, the canvas menu and the Delete key.
                onSelect: () => handleDelete(obj.id),
              },
            ]}
          />
        ))}
      </ul>
    </SidebarPanel>
  );
});

export default MapObjectPanel;
